/**
 * Averdo Buchhandlung - Product Form JavaScript
 *
 * Handles:
 * - Variant selection and URL/price/availability updates
 * - Quantity increment/decrement
 * - Add to cart via Fetch API (POST to /cart/add.js)
 * - Collapsible tab toggles
 * - Copy-to-clipboard for share links
 * - Thumbnail gallery switching
 */

(function () {
  'use strict';

  /* ==========================================================================
     Product Form Custom Element
     ========================================================================== */
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form[data-product-form]');
      this.section = this.closest('[data-product-section]');
      this.variantIdInput = this.querySelector('[data-variant-id]');
      this.addToCartBtn = this.querySelector('[data-add-to-cart]');
      this.addToCartText = this.querySelector('[data-add-to-cart-text]');
      this.cartMessage = this.querySelector('[data-cart-message]');
      this.cartError = this.querySelector('[data-cart-error]');

      this.productData = null;
      this.currentVariant = null;

      this._loadProductData();
      this._bindEvents();
    }

    /**
     * Parse the product JSON from the section's script tag.
     */
    _loadProductData() {
      var section = this.closest('.section-main-product');
      if (!section) return;

      var jsonEl = section.querySelector('script[type="application/json"][id^="ProductJSON"]');
      if (!jsonEl) return;

      try {
        this.productData = JSON.parse(jsonEl.textContent);
        this._setCurrentVariantFromURL();
      } catch (e) {
        console.error('Fehler beim Laden der Produktdaten:', e);
      }
    }

    /**
     * Determine the current variant from the URL parameter or default.
     */
    _setCurrentVariantFromURL() {
      if (!this.productData) return;

      var params = new URLSearchParams(window.location.search);
      var variantId = params.get('variant');

      if (variantId) {
        this.currentVariant = this.productData.variants.find(function (v) {
          return v.id === parseInt(variantId, 10);
        });
      }

      if (!this.currentVariant) {
        this.currentVariant = this.productData.variants.find(function (v) {
          return v.available;
        }) || this.productData.variants[0];
      }
    }

    /**
     * Bind all event listeners.
     */
    _bindEvents() {
      // Form submission (add to cart)
      if (this.form) {
        this.form.addEventListener('submit', this._onSubmit.bind(this));
      }

      // Variant option changes (radio buttons)
      var radioInputs = this.section
        ? this.section.querySelectorAll('[data-product-variants] input[type="radio"]')
        : [];
      radioInputs.forEach(
        function (input) {
          input.addEventListener('change', this._onVariantChange.bind(this));
        }.bind(this)
      );

      // Variant option changes (select dropdowns)
      var selectInputs = this.section
        ? this.section.querySelectorAll('[data-product-variants] select[data-option-index]')
        : [];
      selectInputs.forEach(
        function (select) {
          select.addEventListener('change', this._onVariantChange.bind(this));
        }.bind(this)
      );
    }

    /**
     * Gather currently selected option values.
     */
    _getSelectedOptions() {
      var options = [];

      if (!this.section) return options;

      // Collect from radio buttons
      var variantsContainer = this.section.querySelector('[data-product-variants]');
      if (!variantsContainer) return options;

      // Check for radio groups
      var radioGroups = variantsContainer.querySelectorAll('.product__variant-group');
      radioGroups.forEach(function (group) {
        // Try radio first
        var checked = group.querySelector('input[type="radio"]:checked');
        if (checked) {
          options.push(checked.value);
          return;
        }
        // Try select dropdown
        var select = group.querySelector('select[data-option-index]');
        if (select) {
          options.push(select.value);
        }
      });

      return options;
    }

    /**
     * Handle variant option change events.
     */
    _onVariantChange() {
      if (!this.productData) return;

      var selectedOptions = this._getSelectedOptions();
      if (selectedOptions.length === 0) return;

      // Find matching variant
      var matchedVariant = this.productData.variants.find(function (variant) {
        return variant.options.every(function (opt, index) {
          return opt === selectedOptions[index];
        });
      });

      if (!matchedVariant) return;

      this.currentVariant = matchedVariant;

      this._updateVariantId();
      this._updateURL();
      this._updatePrice();
      this._updateBuyButton();
      this._updateMainImage();
      this._updateOptionLabels(selectedOptions);

      // Dispatch event for other components
      window.dispatchEvent(
        new CustomEvent('variant:changed', {
          detail: {
            variant: this.currentVariant,
            product: this.productData
          }
        })
      );
    }

    /**
     * Update the hidden variant ID input.
     */
    _updateVariantId() {
      if (this.variantIdInput && this.currentVariant) {
        this.variantIdInput.value = this.currentVariant.id;
      }
    }

    /**
     * Update the browser URL with the new variant parameter.
     */
    _updateURL() {
      if (!this.currentVariant) return;

      var url = new URL(window.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /**
     * Update the displayed price based on the selected variant.
     */
    _updatePrice() {
      if (!this.currentVariant) return;

      var priceWrapper = document.getElementById('ProductPrice');
      if (!priceWrapper) return;

      var regularPriceEl = priceWrapper.querySelector('.price__regular');
      var comparePriceEl = priceWrapper.querySelector('.price__compare');
      var priceContainer = priceWrapper.querySelector('.price');
      var saveEl = priceWrapper.querySelector('.product__price-save');

      if (regularPriceEl) {
        regularPriceEl.textContent = this._formatMoney(this.currentVariant.price);
      }

      var hasComparePrice =
        this.currentVariant.compare_at_price &&
        this.currentVariant.compare_at_price > this.currentVariant.price;

      if (priceContainer) {
        priceContainer.classList.toggle('price--on-sale', hasComparePrice);
        priceContainer.classList.toggle('price--sold-out', !this.currentVariant.available);
      }

      if (comparePriceEl) {
        if (hasComparePrice) {
          comparePriceEl.textContent = this._formatMoney(this.currentVariant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      if (saveEl) {
        if (hasComparePrice) {
          var savings = this.currentVariant.compare_at_price - this.currentVariant.price;
          saveEl.textContent = 'Sie sparen ' + this._formatMoney(savings);
          saveEl.style.display = '';
        } else {
          saveEl.style.display = 'none';
        }
      }
    }

    /**
     * Format a Shopify price integer (in cents) to a EUR string.
     */
    _formatMoney(cents) {
      if (typeof cents !== 'number') return '';
      var amount = (cents / 100).toFixed(2).replace('.', ',');
      return amount + ' \u20AC';
    }

    /**
     * Update the buy button state based on variant availability.
     */
    _updateBuyButton() {
      if (!this.addToCartBtn || !this.addToCartText) return;

      if (!this.currentVariant) {
        this.addToCartBtn.disabled = true;
        this.addToCartText.innerHTML = 'Nicht verf\u00FCgbar';
        this.addToCartBtn.classList.add('product__add-to-cart--sold-out');
        return;
      }

      if (this.currentVariant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('product__add-to-cart--sold-out');
        this.addToCartText.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> In den Warenkorb';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('product__add-to-cart--sold-out');
        this.addToCartText.textContent = 'Ausverkauft';
      }
    }

    /**
     * Update the main product image if the variant has a featured image.
     */
    _updateMainImage() {
      if (!this.currentVariant || !this.currentVariant.featured_image) return;

      var mainImage = document.getElementById('ProductMainImage');
      if (!mainImage) return;

      var variantImageId = this.currentVariant.featured_image.id;

      // Update main image src
      mainImage.src = this.currentVariant.featured_image.src;
      mainImage.alt = this.currentVariant.featured_image.alt || this.productData.title;

      // Update active thumbnail
      var thumbnails = this.section
        ? this.section.querySelectorAll('[data-product-thumbnail]')
        : [];
      thumbnails.forEach(function (thumb) {
        thumb.classList.toggle(
          'is-active',
          parseInt(thumb.dataset.imageId, 10) === variantImageId
        );
      });
    }

    /**
     * Update the option value labels beside each option name.
     */
    _updateOptionLabels(selectedOptions) {
      if (!this.section) return;

      selectedOptions.forEach(function (value, index) {
        var label = this.section.querySelector(
          '[data-variant-option-value="' + index + '"]'
        );
        if (label) {
          label.textContent = value;
        }
      }.bind(this));
    }

    /**
     * Handle form submission -- add to cart via Fetch API.
     */
    _onSubmit(event) {
      event.preventDefault();

      if (!this.form || !this.addToCartBtn) return;
      if (this.addToCartBtn.disabled) return;

      // Show loading state
      this.addToCartBtn.classList.add('is-loading');
      this.addToCartBtn.disabled = true;
      this._hideMessages();

      var formData = new FormData(this.form);

      // Get quantity from the quantity input
      var quantityInput = this.section
        ? this.section.querySelector('[data-quantity-input]')
        : null;
      if (quantityInput) {
        formData.set('quantity', quantityInput.value);
      }

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        body: formData
      })
        .then(
          function (response) {
            if (!response.ok) {
              return response.json().then(function (data) {
                throw new Error(data.description || 'Fehler beim Hinzuf\u00FCgen zum Warenkorb');
              });
            }
            return response.json();
          }
        )
        .then(
          function (data) {
            // Success
            this._showCartMessage();
            this._updateCartCount();

            // Dispatch cart update event
            window.dispatchEvent(
              new CustomEvent('cart:updated', {
                detail: { item: data }
              })
            );
          }.bind(this)
        )
        .catch(
          function (error) {
            this._showCartError(error.message);
          }.bind(this)
        )
        .finally(
          function () {
            this.addToCartBtn.classList.remove('is-loading');
            if (this.currentVariant && this.currentVariant.available) {
              this.addToCartBtn.disabled = false;
            }
          }.bind(this)
        );
    }

    /**
     * Show the success message after adding to cart.
     */
    _showCartMessage() {
      if (this.cartMessage) {
        this.cartMessage.classList.add('is-visible');
        setTimeout(
          function () {
            this.cartMessage.classList.remove('is-visible');
          }.bind(this),
          4000
        );
      }
    }

    /**
     * Show an error message.
     */
    _showCartError(message) {
      if (this.cartError) {
        this.cartError.textContent = message;
        this.cartError.classList.add('is-visible');
        setTimeout(
          function () {
            this.cartError.classList.remove('is-visible');
          }.bind(this),
          6000
        );
      }
    }

    /**
     * Hide all messages.
     */
    _hideMessages() {
      if (this.cartMessage) this.cartMessage.classList.remove('is-visible');
      if (this.cartError) this.cartError.classList.remove('is-visible');
    }

    /**
     * Fetch the updated cart to update the header cart count.
     */
    _updateCartCount() {
      fetch(window.routes.cart_url + '.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          window.dispatchEvent(
            new CustomEvent('cart:count-updated', {
              detail: { count: cart.item_count }
            })
          );

          // Update any cart count badges in the header
          var countEls = document.querySelectorAll('[data-cart-count]');
          countEls.forEach(function (el) {
            el.textContent = cart.item_count;
            el.classList.toggle('is-hidden', cart.item_count === 0);
          });
        })
        .catch(function (error) {
          console.error('Warenkorb-Aktualisierung fehlgeschlagen:', error);
        });
    }
  }

  // Register the custom element
  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }

  /* ==========================================================================
     Quantity Selector
     ========================================================================== */
  function initQuantitySelectors() {
    var minusBtns = document.querySelectorAll('[data-quantity-minus]');
    var plusBtns = document.querySelectorAll('[data-quantity-plus]');

    minusBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.product__quantity-wrapper');
        if (!wrapper) return;
        var input = wrapper.querySelector('[data-quantity-input]');
        if (!input) return;

        var currentValue = parseInt(input.value, 10) || 1;
        var minValue = parseInt(input.min, 10) || 1;
        var newValue = Math.max(minValue, currentValue - 1);

        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Update minus button disabled state
        btn.disabled = newValue <= minValue;
      });
    });

    plusBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.product__quantity-wrapper');
        if (!wrapper) return;
        var input = wrapper.querySelector('[data-quantity-input]');
        if (!input) return;

        var currentValue = parseInt(input.value, 10) || 1;
        var maxValue = parseInt(input.max, 10) || 99;
        var newValue = Math.min(maxValue, currentValue + 1);

        input.value = newValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Update minus button state
        var minusBtn = wrapper.querySelector('[data-quantity-minus]');
        if (minusBtn) {
          minusBtn.disabled = false;
        }
      });
    });

    // Validate direct input
    var quantityInputs = document.querySelectorAll('[data-quantity-input]');
    quantityInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var minValue = parseInt(input.min, 10) || 1;
        var maxValue = parseInt(input.max, 10) || 99;
        var value = parseInt(input.value, 10);

        if (isNaN(value) || value < minValue) {
          input.value = minValue;
        } else if (value > maxValue) {
          input.value = maxValue;
        }

        // Update minus button
        var wrapper = input.closest('.product__quantity-wrapper');
        if (wrapper) {
          var minusBtn = wrapper.querySelector('[data-quantity-minus]');
          if (minusBtn) {
            minusBtn.disabled = parseInt(input.value, 10) <= minValue;
          }
        }
      });

      // Prevent non-numeric input
      input.addEventListener('keydown', function (e) {
        // Allow: backspace, delete, tab, escape, enter, arrows
        if (
          [8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
          // Allow Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
          ((e.keyCode === 65 || e.keyCode === 67 || e.keyCode === 86 || e.keyCode === 88) &&
            (e.ctrlKey || e.metaKey))
        ) {
          return;
        }
        // Ensure it is a number key
        if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
          e.preventDefault();
        }
      });
    });
  }

  /* ==========================================================================
     Collapsible Tabs
     ========================================================================== */
  function initCollapsibleTabs() {
    var triggers = document.querySelectorAll('[data-collapsible-trigger]');

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var tab = trigger.closest('[data-collapsible-tab]');
        if (!tab) return;

        var content = tab.querySelector('[data-collapsible-content]');
        if (!content) return;

        var inner = content.querySelector('.product__tab-content-inner');
        var isOpen = tab.classList.contains('is-open');

        if (isOpen) {
          // Close
          content.style.maxHeight = content.scrollHeight + 'px';
          // Force reflow
          content.offsetHeight; // eslint-disable-line no-unused-expressions
          content.style.maxHeight = '0';
          tab.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          // Open
          tab.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          content.style.maxHeight = content.scrollHeight + 'px';

          // Remove max-height after transition for dynamic content
          content.addEventListener(
            'transitionend',
            function handler() {
              if (tab.classList.contains('is-open')) {
                content.style.maxHeight = 'none';
              }
              content.removeEventListener('transitionend', handler);
            }
          );
        }
      });
    });

    // Initialize open-by-default tabs
    var openTabs = document.querySelectorAll('[data-collapsible-tab].is-open');
    openTabs.forEach(function (tab) {
      var content = tab.querySelector('[data-collapsible-content]');
      if (content) {
        content.style.maxHeight = 'none';
      }
    });
  }

  /* ==========================================================================
     Thumbnail Gallery
     ========================================================================== */
  function initThumbnailGallery() {
    var thumbnails = document.querySelectorAll('[data-product-thumbnail]');
    var mainImage = document.getElementById('ProductMainImage');

    if (!mainImage || thumbnails.length === 0) return;

    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        // Update main image
        var newSrc = thumb.dataset.imageSrc;
        var newSrcset = thumb.dataset.imageSrcset;
        var newAlt = thumb.dataset.imageAlt;

        if (newSrc) mainImage.src = newSrc;
        if (newSrcset) mainImage.srcset = newSrcset;
        if (newAlt) mainImage.alt = newAlt;

        // Update active state
        thumbnails.forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ==========================================================================
     Copy URL Share Button
     ========================================================================== */
  function initShareButtons() {
    var copyBtns = document.querySelectorAll('[data-copy-url]');

    copyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.dataset.copyUrl;
        if (!url) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () {
            showCopyFeedback(btn);
          });
        } else {
          // Fallback for older browsers
          var textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            showCopyFeedback(btn);
          } catch (e) {
            console.error('Kopieren fehlgeschlagen:', e);
          }
          document.body.removeChild(textArea);
        }
      });
    });

    function showCopyFeedback(btn) {
      var originalLabel = btn.getAttribute('aria-label');
      btn.setAttribute('aria-label', 'Link kopiert!');
      btn.style.backgroundColor = 'var(--color-primary)';
      btn.style.color = '#fff';

      setTimeout(function () {
        btn.setAttribute('aria-label', originalLabel);
        btn.style.backgroundColor = '';
        btn.style.color = '';
      }, 2000);
    }
  }

  /* ==========================================================================
     Initialize Everything on DOM Ready
     ========================================================================== */
  function init() {
    initQuantitySelectors();
    initCollapsibleTabs();
    initThumbnailGallery();
    initShareButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
